import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import { getListingFullAddressString, getMarketSecurityDepositLabel } from 'common/listingHelpers';

import {
  dataTable,
  actionDataTableLoadItemsIfNotFullyLoaded,
  actionInitializeDataTableState,
} from 'client/helpers/dataTables';

import {
  STRIPE_CHARGE_CATEGORY_SECURITY_DEPOSIT,
  STRIPE_CHARGE_STATUS_PENDING,
  STRIPE_CHARGE_STATUS_SUCCESS,
} from 'common/stripeConstants';

import { SR_API_ACTION_PARTNER_SUBMIT_LISTING_FEE_SETTINGS } from 'api/actionTypes';

import { SCREEN_ID_PARTNER_LISTING_EDIT_FEE_SETTINGS } from 'client/constants/screenIds';

import { DATA_TABLE_SOURCE_PARTNER_LISTINGS } from 'common/dataTableConstants';

import { SR_USER_GROUP_ROLE_PARTNER } from 'common/userRoles';

import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withLocationQuery from 'client/helpers/withLocationQuery';
import withApiState from 'client/helpers/withApiState';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';

import postWithState from 'client/actions/postWithState';
import PartnerActions from 'client/actions/Partner';

import SrBox from 'client/components/SrBox';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrPartnerBankAccount from 'client/components/SrPartnerBankAccount/SrPartnerBankAccount';

function getListingDataTableSettings({ partner, match }) {
  const listingId = parseInt(match.params.listingId, 10);
  if (!partner) {
    return null;
  }
  const dataTableSettings = {
    itemsPropName: 'listing',
    dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS,
    viewId: `by-listingId-${listingId}`,
    count: 1,
    injectSingleItem: true,
    filter: {
      'listings.id': parseInt(listingId, 10),
      'listings.partnerId': partner.id,
    },
    customParams: {
      partnerListings: {},
    },
  };
  return dataTableSettings;
}

@withTransitionHook
@allowedRoles({
  roles: SR_USER_GROUP_ROLE_PARTNER,
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_PARTNER_LISTING_EDIT_FEE_SETTINGS)
@withScrollToTop
@withApiState({
  partnerEditListingApiState: {
    action: SR_API_ACTION_PARTNER_SUBMIT_LISTING_FEE_SETTINGS,
  },
})
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  currentUser: ['currentUser'],
  screenIdHistory: ['analytics', 'screenIdHistory'],
  pathHistory: ['analytics', 'pathHistory'],
}))
@withLocationQuery
@dataTable(getListingDataTableSettings)
class PartnerEditListingFeeSettingsPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Edit Listing | Security Deposit',
  });

  static propTypes = {
    partner: PropTypes.object,
    listing: PropTypes.object,
    isTransitioning: PropTypes.bool.isRequired,
  };

  componentWillUnmount() {
    this._isUnmounting = true;
  }

  static fetchData(match, inject) {
    const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
    const promises = [];

    if (!partner) {
      promises.push(
        PartnerActions.loadPartnerBySlug(inject, {
          slug: match.params.partnerSlug,
        }),
      );
    }

    const listingDataTableSettings = getListingDataTableSettings({ partner, match });
    if (listingDataTableSettings) {
      actionInitializeDataTableState(inject, listingDataTableSettings);
      promises.push(actionDataTableLoadItemsIfNotFullyLoaded(inject, listingDataTableSettings));
    }

    return Promise.all(promises);
  }

  _onSubmit = (feeSettings) => {
    const { dispatch, match } = this.props;
    const listingId = parseInt(match.params.listingId, 10);
    dispatch(postWithState, {
      action: SR_API_ACTION_PARTNER_SUBMIT_LISTING_FEE_SETTINGS,
      payload: {
        listingId,
        feeSettings,
      },
    }).then(() =>
      Promise.all([
        dispatch(PartnerActions.getHouseholdsApplyingToListing, {
          listingId,
          forceRefresh: true,
        }),
        dispatch(PartnerActions.loadListingStripeAccount, {
          listingId,
          forceRefresh: true,
        }),
      ]),
    );
  };

  render() {
    const { match, listing, partner, currentUser, isTransitioning } = this.props;

    const listingId = parseInt(match.params.listingId, 10);

    if (isTransitioning && (!partner || !listing)) {
      return null;
    }

    if (!partner || !listing) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return <Redirect to={userLandingPage} />;
    }

    const stripeCharges = listing.stripeCharges.filter(
      ({ category }) => category === STRIPE_CHARGE_CATEGORY_SECURITY_DEPOSIT,
    );

    const pendingStripeCharge = stripeCharges.find(
      (stripeCharge) =>
        (stripeCharge.status === STRIPE_CHARGE_STATUS_PENDING ||
          stripeCharge.status === STRIPE_CHARGE_STATUS_SUCCESS) &&
        stripeCharge.transferToStripeAccountId !== null,
    );

    return (
      <SrPartnerPageV2
        backUrl={`/partner/${match.params.partnerSlug}/listing/${listingId}`}
        navbarTitle={getListingFullAddressString(listing)}
        pageHeader={getMarketSecurityDepositLabel(listing, true)}
      >
        {!listing ? (
          <div className="loading-gif" />
        ) : (
          <SrBox margin="23px 0">
            <SrPartnerBankAccount
              stripeAccount={listing.stripeAccount}
              listing={listing}
              pendingStripeCharge={!!pendingStripeCharge}
              hideAddBankAccount={pendingStripeCharge}
              alternativeMethodEnabled={!!listing.securityDepositCollectionInstructions}
              whoCollectsSecurityDepositPetFees={listing.whoCollectsSecurityDepositPetFees}
              securityDepositCollectionInstructions={listing.securityDepositCollectionInstructions}
              onSubmit={this._onSubmit}
            />
          </SrBox>
        )}
      </SrPartnerPageV2>
    );
  }
}

PartnerEditListingFeeSettingsPage.displayName = 'PartnerEditListingFeeSettingsPage';

export default PartnerEditListingFeeSettingsPage;
